/**
 * Layer ordering for everything that has to stay readable on top of the route
 * line: the POI overlays (fuel stations, autohofs) and the road number shields
 * (A1, E30, …).
 *
 * The route line grows to ~20px of casing at high zoom, so anything drawn
 * below it is hidden completely, and every route line is added on top of the
 * whole style - the directions layers when the map loads, the reference and
 * driver polylines right underneath them.
 *
 * For the POI overlays that showed up as a timing-dependent bug (cached
 * stations render before the directions control is initialized, freshly
 * fetched ones after it). For the shields it was permanent: they are the
 * topmost layer of the base style, which still leaves them below every route
 * layer, and a motorway shield sits exactly on the road the route follows.
 *
 * All of them are therefore anchored right below the waypoint pins: above
 * every route line, but still below the stop icons and the driver marker,
 * which carry the more important information.
 */

export const ABOVE_ROUTE_ANCHOR_LAYER_IDS = [
  "maplibre-gl-directions-waypoint-icon",
  "maplibre-gl-directions-waypoint-driver-icon",
]

/**
 * Road number shields, rendered into the style by
 * `@americana/maplibre-shield-generator`. `highway-shield-ref` shows the plain
 * `ref` of roads that are in no route relation (most French D roads), below
 * the relation shields. `highway-exit` carries the exit numbers and has the
 * same problem. Styles without these layers are skipped.
 */
export const ROAD_SHIELD_LAYER_IDS = [
  "highway-shield-ref",
  "highway-shield",
  "highway-exit",
]

type LayerOrderingMap = {
  getLayer: (id: string) => unknown
  moveLayer: (id: string, beforeId?: string) => void
}

/**
 * The layer the overlays should be inserted before, or `undefined` when no
 * anchor exists yet (the overlays then go on top of the style).
 */
export function getAboveRouteBeforeId(
  map: Pick<LayerOrderingMap, "getLayer">,
): string | undefined {
  return ABOVE_ROUTE_ANCHOR_LAYER_IDS.find((layerId) => map.getLayer(layerId))
}

/**
 * Re-asserts the stacking of the given layers. Safe to call whenever either
 * they or the route layers are (re)created; missing layers are skipped, and
 * the relative order of `layerIds` is preserved.
 */
export function raiseLayersAboveRouteLines(
  map: LayerOrderingMap,
  layerIds: string[],
): void {
  const beforeId = getAboveRouteBeforeId(map)

  layerIds.forEach((layerId) => {
    if (map.getLayer(layerId)) {
      map.moveLayer(layerId, beforeId)
    }
  })
}
